import { useState } from 'react';

interface GameStats {
  [key: string]: any;
}

interface SendStatsOptions {
  gameKey: string;
  gameDate: string;
  gameId: string;
  gameResult: string;
  stats: GameStats;
}

interface SendHistoricalStatsOptions {
  gameKey: string;
  level: string;
  stats: GameStats;
}

export const useSendStats = (apiBaseUrl: string = 'https://play.hey.gg') => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sendStats = async ({
    gameKey,
    gameDate,
    gameId,
    gameResult,
    stats,
  }: SendStatsOptions) => {
    setLoading(true);
    setError(null);

    const accessToken = localStorage.getItem('heygg_jwt');
    const anonymousId = localStorage.getItem('heygg_anonymous_id');

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }

    const body: any = {
      game_key: gameKey,
      game_date: gameDate,
      game_id: gameId,
      game_result: gameResult,
      stats: stats,
    };

    if (anonymousId) {
      body.anonymous_id = anonymousId;
    }

    try {
      const response = await fetch(`${apiBaseUrl}/api/game-stats`, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        throw new Error('Failed to send stats');
      }

      const data = await response.json();
      setLoading(false);
      return data;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
      setLoading(false);
      throw err;
    }
  };

  const sendHistoricalStats = async ({
    gameKey,
    level,
    stats,
  }: SendHistoricalStatsOptions) => {
    setLoading(true);
    setError(null);

    const accessToken = localStorage.getItem('heygg_jwt');
    const anonymousId = localStorage.getItem('heygg_anonymous_id');

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }

    const body: any = {
      game_key: gameKey,
      level: level,
      stats: stats,
      anonymous_id: anonymousId,
    };

    try {
      const response = await fetch(`${apiBaseUrl}/api/historical-stats`, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(body),
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to send historical stats');
      }

      const data = await response.json();
      setLoading(false);
      return data;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
      setLoading(false);
      throw err;
    }
  };

  return { sendStats, sendHistoricalStats, loading, error };
};
